import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button } from "antd";
import { ConfigProvider } from "@ui-kit/ConfigProvider";
import dayjs from "dayjs";
import { translate } from "@lib/translate";
import { CalendarIcon } from "@icons/CalendarIcon";
import { BaseDatePicker } from "./BaseDatePicker";
import "./DatePicker.css";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { TabButton } from "@ui-kit/TabButton/TabButton";

interface SelectProps {
  /**
   * Callback function to handle the selection of an item.
   */
  onChange: (
    dates: [start: dayjs.Dayjs | null, end: dayjs.Dayjs | null] | null,
  ) => void;
  value: [start: dayjs.Dayjs | null, end: dayjs.Dayjs | null] | null;
}

const formatDateRange = (
  dates: [start: dayjs.Dayjs | null, end: dayjs.Dayjs | null] | null,
) => {
  if (!dates) {
    return "Select dates";
  }

  const [start, end] = dates;
  if (!start || !end) {
    return start?.format("MMM D") ?? "Select dates";
  }

  if (start.month() === end.month()) {
    return `${start.format("MMM D")} - ${end.format("D")}`;
  }

  return `${start.format("MMM D")} - ${end.format("MMM D")}`;
};

const DatePicker: FunctionComponent<SelectProps> = ({ onChange, value }) => {
  const [isOpen, setIsOpen] = useState(false);
  const rangeRef = useRef<any>(null);
  const buttonRef = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !rangeRef.current?.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClear = useCallback(() => {
    onChange(null);
    setIsOpen(false);
  }, [onChange]);

  const handlePress = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const renderPanel = useCallback(
    (panelNode: React.ReactNode) => {
      return (
        <div ref={rangeRef} className="date-picker-panel-component">
          {panelNode}
          <div className="select-actions">
            <Button type="text" onClick={handleClear}>
              {translate("common.clear")}
            </Button>
            <Button
              type="primary"
              style={{ boxShadow: "none" }}
              onClick={handlePress}>
              {translate("common.apply")}
            </Button>
          </div>
        </div>
      );
    },
    [handleClear, handlePress],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const handleChange = useCallback(
    (dates: [start: dayjs.Dayjs | null, end: dayjs.Dayjs | null] | null) => {
      onChange(dates);
    },
    [onChange],
  );

  return (
    <ConfigProvider type="client">
      <div style={{ position: "relative" }}>
        <div
          style={{
            /*
             *  No current way to not have the input, so put it under the button and hide it. The
             *  popover will be based on input's position.
             */
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
          }}>
          <BaseDatePicker
            open={isOpen}
            value={value}
            panelRender={renderPanel}
            onChange={handleChange}
            onOpenChange={handleOpenChange}
          />
        </div>
        <TabButton
          variant="outline"
          ref={buttonRef}
          onPress={handlePress}
          iconPrefix={<CalendarIcon size={16} color={colors.neutral600} />}
          label={formatDateRange(value)}
        />
      </div>
    </ConfigProvider>
  );
};

export { DatePicker };
